/**
 * Scan feedback sounds (Design Brief: every scan answers by ear, so the
 * operator keeps their eyes on the goods). Success: one short high beep.
 * Failure: two low buzzes, and a vibration on handhelds. Made with Web Audio,
 * no files; the context starts on the first scan (a key press).
 */

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  try {
    ctx ??= new AudioContext();
    if (ctx.state === "suspended") void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

function tone(a: AudioContext, start: number, freq: number, dur: number, type: OscillatorType, volume: number) {
  const osc = a.createOscillator();
  const gain = a.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  // Short fades so the beep does not click.
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(volume, start + 0.005);
  gain.gain.setValueAtTime(volume, start + dur - 0.01);
  gain.gain.linearRampToValueAtTime(0, start + dur);
  osc.connect(gain).connect(a.destination);
  osc.start(start);
  osc.stop(start + dur);
}

export function scanOk() {
  const a = audio();
  if (!a) return;
  tone(a, a.currentTime, 1760, 0.09, "sine", 0.25);
}

export function scanFail() {
  const a = audio();
  if (a) {
    tone(a, a.currentTime, 220, 0.16, "square", 0.18);
    tone(a, a.currentTime + 0.22, 180, 0.22, "square", 0.18);
  }
  try {
    navigator.vibrate?.([120, 80, 160]);
  } catch {
    // not a handheld
  }
}

/**
 * What a scan handler returns: true or false, or a promise that resolves
 * (success; false also fails) or rejects (failure). Nothing: no sound.
 */
export type ScanResult = boolean | void | Promise<unknown>;

export function soundFor(result: ScanResult) {
  if (result === true) scanOk();
  else if (result === false) scanFail();
  else if (result instanceof Promise) {
    result.then(
      (v) => (v === false ? scanFail() : scanOk()),
      () => scanFail(),
    );
  }
}
